import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, UnstyledButton, Group, Text, Center, Pagination, Flex, ScrollArea, Button, useMantineTheme, Skeleton, Modal, Menu, Drawer, Divider, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronUp, IconChevronDown, IconSelector, IconSearch, IconPencil, IconChartArrowsVertical, IconBox, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { notifications } from '@mantine/notifications';
import { getBenefits, deleteBenefit } from '../../../services/api.service';
import BenefitForm from '../../forms/BenefitForm';
import dayjs from 'dayjs';
import BenefitLevelForm from '../../forms/BenefitLevelForm';
import BenefitCodeForm from '../../forms/BenefitCodeForm';
import BenefitLevelsList from './BenefitLevelsList';
import BenefitCodesList from './BenefitCodesList';
import { modals } from '@mantine/modals';
import DeleteButton from '../../buttons/DeleteButton';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const BenefitsTab = () => {

  const dispatch = useDispatch();

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [benefits, setBenefits] = useState([]);
	const [allBenefits, setAllBenefits] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [benefitEdit, setBenefitEdit] = useState({});
	const [benefitLevel, setBenefitLevel] = useState({});
	const [benefitStock, setBenefitStock] = useState({});
	const [sortedData, setSortedData] = useState([]);
	const [sortBy, setSortBy] = useState(null);
	const [reverseSortDirection, setReverseSortDirection] = useState(false);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const [ldOpened, ldActions] = useDisclosure(false);
	const [sdOpened, sdActions] = useDisclosure(false);
	const [rs, reloadStock] = useState(0);
	const [rl, reloadLevels] = useState(0);
	const [total, setTotal] = useState(0);
	const [limit] = useState(10);
	const [page, setPage] = useState(1);
	const offset = page - 1;
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);
	const [searchParams] = useSearchParams();
	const { tenant } = useSelector(state => state.authData);	

	// On load
	useEffect(() => {

		// Get list
		getBenefitsList();

		// Check query params to open form
		if (searchParams.get('add') === '1') open();

		// eslint-disable-next-line
	}, []);

	// Get benefits
	const getBenefitsList = () => {

		// Get
		getBenefits(`?limit=9999&offset=0`, function (response) {

			// Set benefits and initial sorted data, remove loader
			setBenefits(response.data.data);
			setSortedData(response.data.data);
			setTotal(response.data.total);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, reversed, sorted, onSort }) => {
		return (
			<th className={classes.th}>
				<UnstyledButton onClick={onSort} className={classes.control}>
					<Group position="apart">
						<Text fw={700} fz="sm">
							{children}
						</Text>
						<Center className={classes.icon}>
							{sorted ?
								(reversed ?
									<IconChevronUp size="0.9rem" stroke={1.5} />
									:
									<IconChevronDown size="0.9rem" stroke={1.5} />
								)
								: <IconSelector size="0.9rem" stroke={1.5} />}
						</Center>
					</Group>
				</UnstyledButton>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th
				sorted={sortBy === 'id'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('id')}
			>
				ID
			</Th>
			<Th
				sorted={sortBy === 'active'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('active')}
			>
				Activo
			</Th>
			<Th
				sorted={sortBy === 'hasStock'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('hasStock')}
			>
				Stock
			</Th>
			<Th
				sorted={sortBy === 'name'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('name')}
			>
				Nombre
			</Th>
			<Th
				sorted={sortBy === 'startDate'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('startDate')}
			>
				Inicio
			</Th>
			<Th
				sorted={sortBy === 'endDate'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('endDate')}
			>
				Finalización
			</Th>
			<Th
				sorted={sortBy === 'partner'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('partner')}
			>
				Partner
			</Th>
			{!tenant &&
				<Th
					sorted={sortBy === 'tenant'}
					reversed={reverseSortDirection}
					onSort={() => setSorting('tenant')}
				>
					Cliente
				</Th>
			}
			<Th
				sorted={sortBy === 'category'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('category')}
			>
				Categoría
			</Th>
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
					color="black"
				></Text>
			</th>
		</tr>
	);

	// Build rows
	const dataToShow = search.trim()
		? sortedData.slice((page - 1) * limit, page * limit)
		: benefits.slice((page - 1) * limit, page * limit);

	const rows = dataToShow.map((element) => (
		<Menu
			shadow="md"
			width={200}
			position="bottom-start"
			key={element.id}
			withinPortal
		>
			{/* Opener */}
			<Menu.Target>
				<tr style={{ cursor: 'pointer' }}>
					<td width={100}>
						{element.id}
					</td>
					<td width={120}>
						{element.active ? 'Si' : 'No'}
					</td>
					<td width={120}>
						{element.hasStock ? 'Si' : 'No'}
					</td>
					<td>
						{element.name}
					</td>
					<td>
						{dayjs(new Date(element.startDate)).format('DD-MM-YYYY HH:mm')}
					</td>
					<td>
						{dayjs(new Date(element.endDate)).format('DD-MM-YYYY HH:mm')}
					</td>
					<td>
						({element.partner?.id}) {element.partner?.name}
					</td>
					{!tenant &&
						<td>
							{element.tenant ? element.tenant?.name : 'NA'}
						</td>
					}
					<td>
						({element.category?.id}) {element.category?.name}
					</td>
					<td>
						<DeleteButton
							onClick={(e) => {
								e.stopPropagation();
								openDeleteModal(element.id);
							}}
						/>
					</td>
				</tr>
			</Menu.Target>
			{/* Content */}
			<Menu.Dropdown>
				{element.hasStock ?
					!tenant && <Menu.Item
						icon={<IconBox size={14} />}
						onClick={(event) => toggleBenefitStock(event, element.id)}
					>
						Administrar Inventario
					</Menu.Item>
					: null}
				<Menu.Item
					icon={<IconPencil size={14} />}
					onClick={(event) => toggleBenefitEdit(event, element)}
				>
					Editar Beneficio
				</Menu.Item>
				{!tenant && <Menu.Item
					icon={<IconChartArrowsVertical size={14} />}
					onClick={(event) => toggleBenefitLevel(event, element)}
				>
					Administrar Niveles
				</Menu.Item>}
			</Menu.Dropdown>
		</Menu>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);

		if (value.trim()) {
			const query = value.toLowerCase().trim();
			const filtered = benefits.filter((item) =>
				keys(benefits[0]).some((key) =>
					item[key]?.toString().toLowerCase().includes(query)
				)
			);
			setSortedData(filtered);
			setPage(1);
		} else {
			setSortedData([]);
			setPage(1);
		}
	};

	// Get current sort
	const setSorting = (field) => {
		const reversed = field === sortBy ? !reverseSortDirection : false;
		setReverseSortDirection(reversed);
		setSortBy(field);
		setSortedData(sortData(benefits, { sortBy: field, reversed, search }));
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		// return data.filter((item) => keys(data[0]).some((key) => item?.[key]?.toString().toLowerCase().includes(query)));
		return data.filter(item => {
			return Object.values(item).some(value => {
				if (typeof value === 'object' && value !== null) {
					// Recurse into nested objects
					return filterData([value], search).length > 0;
				} else if (typeof value === 'string') {
					// Check string values
					return value.toLowerCase().includes(query);
				}
				return false; // Ignore other types (numbers, booleans, etc.)
			});
		});
	}

	// Sort data
	const sortData = (data, payload) => {

		// Assign
		const { sortBy } = payload;

		// No filter
		if (!sortBy) { return filterData(data, payload.search); }

		// Return filtered data
		return filterData(
			[...data].sort((a, b) => {
				if (payload.reversed) {
					return b[sortBy].toString().localeCompare(a[sortBy]);
				}

				return a[sortBy].toString().localeCompare(b[sortBy]);
			}),
			payload.search
		);

	}

	// Close benefits modal
	const closeModal = () => {
		// Get benefits and close modal
		getBenefitsList();
		setIsEdit(false);
		setBenefitEdit({});
		close();
	}

	// Toggle benefit edit
	const toggleBenefitEdit = (event, benefit) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setBenefitEdit(benefit);
		// Open modal
		open();
	}

	// Toggle benefit level
	const toggleBenefitLevel = (event, benefitID) => {
		// Stop click and toggle level
		event.stopPropagation();
		setBenefitLevel(benefitID);
		// Open drawer
		ldActions.open();
	}

	// Toggle benefit stock
	const toggleBenefitStock = (event, benefitID) => {
		// Stop click and toggle level
		event.stopPropagation();
		setBenefitStock(benefitID);
		// Open drawer
		sdActions.open();
	}

	// Liste to page changes
	useEffect(() => {
		if (isLoading) return;

		if (search.trim()) return;

		getBenefitsList();

		// eslint-disable-next-line
	}, [page]);

	// Open delete confirmation modal
	const openDeleteModal = (id) =>
		modals.openConfirmModal({
			title: 'Eliminar Beneficio',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar este beneficio? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar beneficio', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => { }, // Nothing
			onConfirm: () => triggerDeleteBenefit(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete benefit
	const triggerDeleteBenefit = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteBenefit(id, function () {

			// Show success
			notifications.show({
				title: 'Beneficio eliminado correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

  const closeLevelBenefitsModal = () => {
    dispatch.editBenefitLevel.toggleEditing(false);
    dispatch.editBenefitLevel.setBenefitLevel({});

    ldActions.close();
  }

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Benefit Form */}
				<BenefitForm
					closeModal={closeModal}
					isEdit={isEdit}
					benefit={benefitEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
				: null}
			{/* Levels Drawer */}
			<Drawer
				opened={ldOpened}
				onClose={closeLevelBenefitsModal}
				title="Niveles de Beneficio"
				position="right"
				size={700}
			>
				{/* Form */}
				<BenefitLevelForm
					benefit={benefitLevel}
					reload={() => reloadLevels(rl + 1)}
				/>
				<Divider my={30} />
				{/* List */}
				<BenefitLevelsList
					benefit={benefitLevel}
					reload={rl}
				/>
			</Drawer>
			{/* Stock Drawer */}
			<Drawer
				opened={sdOpened}
				onClose={sdActions.close}
				title="Inventario de Beneficio"
				position="right"
				size={600}
			>
				{/* Form */}
				<BenefitCodeForm
					benefit={benefitStock}
					reload={() => reloadStock(rs + 1)}
				/>
				<Divider my={30} />
				{/* List */}
				<BenefitCodesList
					benefit={benefitStock}
					reload={rs}
				/>
			</Drawer>
			{/* Content */}
			<Flex direction="column">
				{/* Top */}
				<Flex mb="md">
					{/* New Benefit */}
					<Button
						onClick={open}
						variant="filled"
					>
						Nuevo Beneficio
					</Button>
					{/* Search */}
					<TextInput
						placeholder="Buscar beneficios"
						icon={<IconSearch size="0.9rem" stroke={1.5} />}
						value={search}
						onChange={handleSearchChange}
						ml="sm"
						sx={{
							width: '100%',
							maxWidth: 350
						}}
						variant="filled"
					/>
				</Flex>
				{/* Table */}
				<ScrollArea>
					{isLoading ?
						<>
							<Skeleton height={40} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
							<Skeleton height={32} mt={12} radius="sm" />
						</>
						:
						<Table
							highlightOnHover
							sx={{ flex: 2 }}
						>
							<thead>{ths}</thead>
							<tbody>{rows.length > 0 ? (
								rows
							) : (
								<tr>
									<td colSpan={7}>
										<Text
											weight={500}
											align="center"
											my="xl"
											color="dimmed"
										>
											Sin resultados
										</Text>
									</td>
								</tr>
							)}</tbody>
						</Table>
					}
				</ScrollArea>
			</Flex>
			{/* Pages */}
			<Center sx={{ marginTop: 20 }}>
				<Pagination
					total={search.trim()
						? Math.ceil(sortedData.length / limit)
						: Math.ceil(total / limit)
					}
					siblings={1}
					defaultValue={1}
					withEdges
					onChange={setPage}
					value={page}
				/>
			</Center>
		</>
	);

}

export default BenefitsTab;
